import { tv } from "@ui/lib/tv";

export type SurfaceSize = "sm" | "lg" | "xl";
export type SurfaceChrome = "card" | "bare";

/**
 * The full-viewport travel container hosting the backdrop and the traveling
 * panel. Both tokens come from the app-chrome layer scale in theme.css.
 */
export const surfaceView = tv({
  base: "z-(--z-sheet) h-(--height-viewport)",
});

/**
 * Silk's `Sheet.Content`, which it styles with `!important`:
 * `overflow: visible`, `margin: 0`, `position: relative`, `box-sizing`. Only
 * sizing survives here — appearance and centering belong on the panel inside.
 *
 * `h-auto` overrides Silk's own panel height (50% of the view at bottom
 * placement, 100% at center) so a surface sizes to its content within the cap.
 *
 * `bg-transparent` overrides Silk's own `background-color: white` on this
 * element. The panel inside carries the fill, and this element spans the full
 * view width, so leaving Silk's default paints an opaque band across the
 * viewport behind every surface.
 */
export const surfaceContent = tv({
  base: "w-full bg-transparent outline-none",
  variants: {
    fill: {
      false: "h-auto",
      true: "h-full",
    },
  },
});

/**
 * The visible surface, one element inside `Sheet.Content` because Silk's
 * `!important` rules make the Content element itself unable to clip or centre
 * anything. Everything appearance-bearing lives here: fill, radius, shadow,
 * width cap, and the clip that makes the radius actually cut its children.
 *
 * `overflow-clip` rather than `hidden`: the panel must never scroll — the body
 * is the scroll container — and `hidden` still scrolls programmatically, so
 * focusing a control deep in a scrolled body would drag the header out of
 * view. `clip` cannot.
 */
export const surfacePanel = tv({
  base: "relative mx-auto flex h-full w-full flex-col overflow-clip",
});

/**
 * The detached desktop card, shared by every surface: at desktop widths a Page
 * and a Dialog are the same object and differ only in width. Declared once
 * because two copies had already drifted — one kept its inline padding under
 * `chrome="bare"` and the other did not.
 *
 * Every rule is compound on `platform: "desktop"` so a mobile skin never has
 * to undo one. Surfaces extend this and add their own mobile platforms.
 */
export const desktopCard = tv({
  compoundVariants: [
    {
      class: "max-h-[calc(100dvh-8rem)] rounded-3xl bg-white px-5 shadow-md",
      platform: "desktop",
    },
    { chrome: "bare", class: "rounded-none bg-transparent px-0 shadow-none", platform: "desktop" },
    { class: "max-w-[460px]", platform: "desktop", size: "sm" },
    { class: "max-w-[588px]", platform: "desktop", size: "lg" },
    { class: "max-w-[920px]", platform: "desktop", size: "xl" },
  ],
  defaultVariants: {
    chrome: "card",
    size: "sm",
  },
  variants: {
    chrome: {
      bare: "",
      card: "",
    },
    platform: {
      ios: "",
      android: "",
      desktop: "",
    },
    size: {
      sm: "",
      lg: "",
      xl: "",
    },
  },
});
